import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Link as MuiLink
} from '@mui/material';
import { AuthUtils, LoginCredentials, AuthResponse } from '../../utils/auth';

interface LoginFormProps {
  onSuccess?: (user: any) => void;
  onSwitchToRegister?: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSuccess, onSwitchToRegister }) => {
  const [credentials, setCredentials] = useState<LoginCredentials>({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Client-side password processing
      const processedPassword = await AuthUtils.processPassword(credentials.password);

      // Send to backend
      const response = await fetch('/api/v1/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include HTTP-only cookies
        body: JSON.stringify({
          username: credentials.username,
          password_hash: processedPassword
        })
      });

      const result: any = await response.json();

      if (response.ok && result?.success && result.user) {
        onSuccess?.(result.user);
      } else {
        // Prefer backend 'detail' if FastAPI raised HTTPException
        const detail = typeof result?.detail === 'string' ? result.detail : undefined;
        const message = result?.message || detail || 'Přihlášení se nezdařilo';
        setError(message);
      }
    } catch (err) {
      setError('Chyba připojení k serveru');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof LoginCredentials) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCredentials(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  return (
    <Box sx={{ maxWidth: 400, mx: 'auto', p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom align="center">
        Přihlášení
      </Typography>

      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          margin="normal"
          label="Uživatelské jméno nebo email"
          type="text"
          value={credentials.username}
          onChange={handleChange('username')}
          disabled={loading}
          required
          sx={{
            '& .MuiInputLabel-root.Mui-focused': {
              color: '#1F1F1F',
            },
          }}
        />

        <TextField
          fullWidth
          margin="normal"
          label="Heslo"
          type="password"
          value={credentials.password}
          onChange={handleChange('password')}
          disabled={loading}
          required
          sx={{
            '& .MuiInputLabel-root.Mui-focused': {
              color: '#1F1F1F',
            },
          }}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading}
          sx={{ mt: 3, mb: 2 }}
        >
          {loading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Přihlašuji...
            </>
          ) : (
            'Přihlásit se'
          )}
        </Button>

        {onSwitchToRegister && (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2">
              Nemáte účet?{' '}
              <MuiLink
                component="button"
                type="button"
                onClick={onSwitchToRegister}
                sx={{ cursor: 'pointer', color: '#1F1F1F', fontWeight: 500 }}
              >
                Zaregistrujte se
              </MuiLink>
            </Typography>
          </Box>
        )}
      </form>
    </Box>
  );
};

export default LoginForm;
